import { createRequest, FetchFunction, IFetchOptions } from "../utilities/fetch";
import { getScenarioEvent, IScenario, startScenario } from "../utilities/scenario";
import { IEventDispatch } from "./dispatch";
import { defer } from "./promise";

export type OperationFunction<T> = (
  createRequest: <T, P extends any[]>(fetchFunction: FetchFunction<T, P>, options?: IFetchOptions) => (...args: P) => Promise<T>,
  dispatchChange: <E extends { changeType: string }>(change: E) => void,
  operationScenario: IScenario<T>
) => Promise<T>;

export function executeOperation<T>(operationName: string, eventDispatch: IEventDispatch, operationFunction: OperationFunction<T>): Promise<T> {
  const { promise, reject, resolve } = defer<T>();

  // Create the scenario for the operation.
  const operationScenario = startScenario<T>(promise, { scenarioName: operationName, scenarioType: "operationExecute" }, (scenarioDetails) => {
    eventDispatch.dispatchEvent("telemetryAvailable", getScenarioEvent("operationComplete", scenarioDetails));
  });

  // Call the delegate with the operation details.
  operationFunction(_createRequest, dispatchChange, operationScenario).then(resolve, reject);

  return operationScenario.scenarioPromise;

  function _createRequest<T, P extends any[]>(fetchFunction: FetchFunction<T, P>, options?: IFetchOptions): (...args: P) => Promise<T> {
    // Generate a function used the execute the underlying network request
    const requestFunction = createRequest(fetchFunction, { eventDispatch, ...options });

    return (...args: P): Promise<T> => {
      const { name = "__unnamed__" } = options || {};
      const startTime = Date.now();

      // Call the underlying fetch function and ensure it is canceled on unmount.
      const fetchPromise = requestFunction(...args);

      // Attach this request to the operation scenario.
      operationScenario.waitUntil<T>(fetchPromise, { scenarioName: name, scenarioType: "networkRequest", startTime });

      return fetchPromise;
    };
  }

  function dispatchChange(change: { changeType: string }): void {
    eventDispatch.dispatchEvent("dataChanged", change);
  }
}
